import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Target, 
  Building2, 
  TrendingUp, 
  DollarSign,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Users,
  RefreshCw,
  Bell,
  Plus,
  UserPlus
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useDashboardStore } from '@/store/dashboardStore';
import { formatCurrency, formatDate } from '@/lib/utils';
import { LeadFormDialog } from '@/components/leads/LeadFormDialog';
import { ContactFormDialog } from '@/components/contacts/ContactFormDialog';
import { isPast, isToday } from 'date-fns';

const priorityColors: Record<string, string> = {
  low: 'bg-gray-100 text-gray-800 border-gray-300',
  medium: 'bg-blue-100 text-blue-800 border-blue-300',
  high: 'bg-orange-100 text-orange-800 border-orange-400',
  urgent: 'bg-red-100 text-red-800 border-red-400',
};

export function DashboardPage() {
  const navigate = useNavigate();
  const { 
    stats, 
    pipelineSummary, 
    recentLeads,
    recentDeals,
    upcomingActivities,
    upcomingReminders,
    isLoading, 
    refreshAll,
    subscribeToRealtime 
  } = useDashboardStore();
  const [showLeadDialog, setShowLeadDialog] = useState(false);
  const [showContactDialog, setShowContactDialog] = useState(false);

  useEffect(() => {
    refreshAll();
    const unsubscribe = subscribeToRealtime();
    return () => unsubscribe();
  }, [refreshAll, subscribeToRealtime]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <div className="flex items-center gap-2">
          <Button onClick={() => setShowLeadDialog(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            New Lead
          </Button>
          <Button variant="outline" onClick={() => setShowContactDialog(true)} className="gap-2">
            <UserPlus className="h-4 w-4" />
            New Contact
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Leads
            </CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total_leads || 0}</div>
            <p className="text-xs text-muted-foreground">
              {stats?.new_leads || 0} new this month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Conversion Rate
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.conversion_rate || 0}%</div>
            <p className="text-xs text-muted-foreground">
              {stats?.converted_leads || 0} converted
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Open Deals
            </CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.open_deals || 0}</div>
            <p className="text-xs text-muted-foreground">
              In pipeline
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Pipeline Value
            </CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(stats?.pipeline_value || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              Total potential
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Second row of KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Deals Won
            </CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats?.deals_won || 0}</div>
            <p className="text-xs text-muted-foreground">
              {formatCurrency(stats?.deals_won_value || 0)} value
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Deals Lost
            </CardTitle>
            <XCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats?.deals_lost || 0}</div>
            <p className="text-xs text-muted-foreground">
              This month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Activities Due Today
            </CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.activities_due_today || 0}</div>
            <p className="text-xs text-muted-foreground">
              Tasks pending
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Overdue Activities
            </CardTitle>
            <AlertCircle className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{stats?.activities_overdue || 0}</div>
            <p className="text-xs text-muted-foreground">
              Needs attention
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Pipeline Summary */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Pipeline Overview</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => navigate('/deals')}>
            View Deals
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {pipelineSummary.map((stage) => (
              <div
                key={stage.stage}
                className="p-3 rounded-lg border cursor-pointer hover:shadow-md transition-shadow"
                style={{ borderLeftColor: stage.stage_color, borderLeftWidth: '4px' }}
                onClick={() => navigate('/deals')}
              >
                <p className="text-xs font-medium text-muted-foreground truncate">{stage.stage}</p>
                <p className="text-2xl font-bold">{stage.deal_count}</p>
                <p className="text-xs text-muted-foreground">
                  {formatCurrency(stage.total_value)}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent Data Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Leads */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Recent Leads
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/leads')}>
              View All
            </Button>
          </CardHeader>
          <CardContent>
            {recentLeads.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No leads yet</p>
            ) : (
              <div className="space-y-3">
                {recentLeads.map((lead) => (
                  <div
                    key={lead.id}
                    className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 cursor-pointer"
                    onClick={() => navigate(`/leads/${lead.id}`)}
                  >
                    <div>
                      <p className="font-medium text-sm">{lead.first_name} {lead.last_name}</p>
                      <p className="text-xs text-muted-foreground">{lead.company_name || 'No company'}</p>
                    </div>
                    <Badge className={
                      lead.status === 'new' ? 'bg-blue-100 text-blue-800' :
                      lead.status === 'qualified' ? 'bg-green-100 text-green-800' :
                      'bg-gray-100 text-gray-800'
                    } variant="secondary">
                      {lead.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Deals */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Recent Deals
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/deals')}>
              View All
            </Button>
          </CardHeader>
          <CardContent>
            {recentDeals.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No deals yet</p>
            ) : (
              <div className="space-y-3">
                {recentDeals.map((deal) => (
                  <div
                    key={deal.id}
                    className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 cursor-pointer"
                    onClick={() => navigate(`/deals/${deal.id}`)}
                  >
                    <div>
                      <p className="font-medium text-sm">{deal.title}</p>
                      <p className="text-xs text-muted-foreground">{formatCurrency(deal.value)}</p>
                    </div>
                    <Badge 
                      style={{ backgroundColor: deal.stage_color || '#6b7280' }}
                      className="text-white"
                    >
                      {deal.stage}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Activities */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Upcoming Activities
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/activities')}>
              View All
            </Button>
          </CardHeader>
          <CardContent>
            {upcomingActivities.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No upcoming activities</p>
            ) : (
              <div className="space-y-3">
                {upcomingActivities.map((activity) => (
                  <div
                    key={activity.id}
                    className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50"
                  >
                    <div>
                      <p className="font-medium text-sm">{activity.subject}</p>
                      <p className="text-xs text-muted-foreground capitalize">{activity.type}</p>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {activity.due_date ? formatDate(activity.due_date) : '-'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Reminders */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Upcoming Reminders
          </CardTitle>
        </CardHeader>
        <CardContent>
          {upcomingReminders.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No pending reminders</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {upcomingReminders.map((reminder) => {
                const dueDate = new Date(reminder.due_date);
                const isOverdue = isPast(dueDate) && !isToday(dueDate);
                const isDueToday = isToday(dueDate);
                const isHighPriority = reminder.priority === 'high' || reminder.priority === 'urgent';
                
                return (
                  <div
                    key={reminder.id}
                    className={`p-3 rounded-lg border-l-4 ${priorityColors[reminder.priority]} ${isHighPriority ? 'ring-1 ring-offset-1' : ''} cursor-pointer hover:shadow-md transition-shadow`}
                    onClick={() => {
                      if (reminder.lead_id) navigate(`/leads/${reminder.lead_id}`);
                      else if (reminder.deal_id) navigate(`/deals/${reminder.deal_id}`);
                    }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{reminder.title}</p>
                        <p className={`text-xs mt-1 ${isOverdue ? 'text-red-600 font-medium' : isDueToday ? 'text-orange-600' : 'text-muted-foreground'}`}>
                          {isOverdue ? '⚠️ Overdue: ' : isDueToday ? '📅 Today: ' : ''}
                          {formatDate(reminder.due_date)}
                        </p>
                      </div>
                      <Badge variant="outline" className={`text-xs shrink-0 ${priorityColors[reminder.priority]}`}>
                        {reminder.priority}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Refresh Button */}
      <div className="flex justify-end">
        <Button variant="outline" onClick={() => refreshAll()} className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Refresh Data
        </Button>
      </div>

      {/* Quick Action Dialogs */}
      <LeadFormDialog 
        open={showLeadDialog} 
        onOpenChange={setShowLeadDialog} 
      />
      <ContactFormDialog 
        open={showContactDialog} 
        onOpenChange={setShowContactDialog} 
      />
    </div>
  );
}
